import { useState } from "react";
import type { FormEvent } from "react";

const FORM_ID = "daijggrp14a1h3mr759g";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daijggrp14a1h3mr759g";

type Status = "idle" | "submitting" | "success" | "error";

const TOPIC_OPTIONS = ["爆料线索", "后续进展", "当事人回应", "反馈与建议"];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : String(serverMsg || "提交失败，请稍后重试，或直接发送邮件到编辑部。"),
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交。你也可以直接发送邮件到编辑部邮箱。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-secondary-200 bg-secondary-50 p-8 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-checkbox-circle-line text-3xl" />
        </span>
        <h3 className="mb-2 font-heading text-lg font-bold text-secondary-900">线索已收到</h3>
        <p className="mb-5 max-w-md text-sm leading-relaxed text-secondary-800">
          编辑部会在核对后决定是否建档追踪。如果线索需要补充材料，我们会通过你留下的邮箱联系你。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 px-5 py-2.5 font-label text-sm text-secondary-800 transition-colors hover:bg-secondary-100"
        >
          <i className="ri-add-line" />
          再投递一条
        </button>
      </div>
    );
  }

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300 bg-background-50 p-5 md:p-6"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="contact-name"
            className="mb-1.5 block font-label text-sm text-foreground-700"
          >
            怎么称呼你
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="例如：热心读者小周"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="mb-1.5 block font-label text-sm text-foreground-700"
          >
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于后续补充材料沟通"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-4">
        <label
          htmlFor="contact-topic"
          className="mb-1.5 block font-label text-sm text-foreground-700"
        >
          线索类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue={TOPIC_OPTIONS[0]}
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          {TOPIC_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <label htmlFor="contact-message" className="block font-label text-sm text-foreground-700">
            线索详情
          </label>
          <span className="font-label text-xs text-foreground-400">最多 500 字</span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请尽量写明时间点、原始出处，以及你能够确认的细节。"
          className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="field-guard"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm leading-relaxed text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {formError}
        </p>
      ) : null}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意编辑部为核实线索而与你取得联系。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
      </div>
    </form>
  );
}